/**
 * The facet rail.
 *
 * Board 03, the left column: a year histogram over the date range, then
 * publication, section, content type, author, origin and pipeline as counted
 * checkbox lists, then the confidence floor.
 *
 * **A facet group appears only when the API counted it.** An empty group is
 * omitted rather than drawn with zeros, because a zero the server never
 * computed is a claim the interface cannot support. That is the same rule as
 * everywhere else in the product: never present a gap as a measurement.
 */

import { useState } from 'react'
import type { Facets, FacetValue } from '../../lib/api'
import type { SearchState } from './model'
import { n } from './model'

/**
 * One year of the histogram. `held` says whether the archive holds an edition
 * for that year at all, which is a different fact from whether the query
 * matched anything in it, and the two are drawn differently.
 */
export interface YearBar {
  year: number
  count: number
  held: boolean
}

interface RailProps {
  state: SearchState
  facets: Facets | undefined
  years: YearBar[]
  onChange: (next: SearchState) => void
  onClear: () => void
  apiHref: string
}

export function FacetRail({ state, facets, years, onChange, onClear, apiHref }: RailProps) {
  const confidencePct = state.min_confidence ? Math.round(Number(state.min_confidence) * 100) : 50

  return (
    <div className="flex flex-col gap-[18px] px-[15px] pb-5 pt-3.5">
      <DateRange state={state} facets={facets} years={years} onChange={onChange} />

      <FacetGroup
        title="Publication"
        rows={facets?.publications}
        selected={state.publication ? [state.publication] : []}
        onToggle={(value) =>
          onChange({ ...state, publication: state.publication === value ? '' : value, offset: 0 })
        }
      />
      <FacetGroup
        title="Section"
        rows={facets?.sections}
        selected={state.section ? [state.section] : []}
        onToggle={(value) =>
          onChange({ ...state, section: state.section === value ? '' : value, offset: 0 })
        }
      />
      <FacetGroup
        title="Content type"
        rows={facets?.types}
        selected={state.types}
        onToggle={(value) =>
          onChange({
            ...state,
            types: state.types.includes(value)
              ? state.types.filter((entry) => entry !== value)
              : [...state.types, value],
            offset: 0,
          })
        }
      />
      <FacetGroup
        title="Author"
        rows={facets?.authors}
        selected={state.author ? [state.author] : []}
        onToggle={(value) =>
          onChange({ ...state, author: state.author === value ? '' : value, offset: 0 })
        }
      />
      <FacetGroup
        title="Origin"
        rows={originRows(facets)}
        selected={state.wire === 'true' ? ['wire'] : state.wire === 'false' ? ['local'] : []}
        onToggle={(value) => {
          const next = value === 'wire' ? 'true' : 'false'
          onChange({ ...state, wire: state.wire === next ? '' : next, offset: 0 })
        }}
      />
      <FacetGroup
        title="Pipeline"
        rows={pipelineRows(facets)}
        selected={state.pipeline ? [state.pipeline] : []}
        onToggle={(value) =>
          onChange({
            ...state,
            pipeline: state.pipeline === value ? '' : (value as 'A' | 'B'),
            offset: 0,
          })
        }
      />

      <ConfidenceFloor
        state={state}
        facets={facets}
        percent={confidencePct}
        onChange={onChange}
      />

      <div className="flex flex-col gap-[7px] border-t border-n300 pt-3.5">
        <button
          type="button"
          onClick={onClear}
          className="self-start bg-transparent p-0 text-left text-[13px] text-accent underline underline-offset-2"
        >
          Clear all facets
        </button>
        <a href={apiHref} className="text-[13px]">
          Search this query in the API
        </a>
      </div>
    </div>
  )
}

function originRows(facets: Facets | undefined): FacetValue[] | undefined {
  if (!facets) return undefined
  const { local, wire } = facets.origin
  if (!local && !wire) return []
  return [
    { value: 'local', label: 'Local', count: local },
    { value: 'wire', label: 'Wire', count: wire },
  ]
}

function pipelineRows(facets: Facets | undefined): FacetValue[] | undefined {
  if (!facets) return undefined
  const { A, B } = facets.pipeline
  if (!A && !B) return []
  return [
    { value: 'A', label: 'A · digital', count: A },
    { value: 'B', label: 'B · scanned', count: B },
  ]
}

/**
 * The year histogram and the two date boxes.
 *
 * Every bar is a counted year, drawn in one of three states, because a reader
 * planning a search has to tell three different facts apart: the archive holds
 * editions for this year and the query matched (an accent bar in proportion),
 * it holds editions and the query matched nothing (a neutral stub), or it
 * holds no edition at all (a magenta stub, the proof-reader's mark for
 * something absent rather than something empty).
 */
function DateRange({
  state,
  facets,
  years,
  onChange,
}: {
  state: SearchState
  facets: Facets | undefined
  years: YearBar[]
  onChange: (next: SearchState) => void
}) {
  const span =
    facets?.earliest && facets?.latest
      ? `${facets.earliest.slice(0, 4)}–${facets.latest.slice(0, 4)}`
      : years.length > 0
        ? `${years[0].year}–${years[years.length - 1].year}`
        : null
  const peak = Math.max(1, ...years.map((entry) => entry.count))
  const ticks = axisTicks(years)

  return (
    <div>
      <div className="mb-[9px] flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink2">
          Date range
        </div>
        {span && <div className="font-mono text-[10px] text-ink">{span}</div>}
      </div>

      {years.length > 0 && (
        <>
          {/*
            The bars grow to fill the rail and stop at 10px, so a fifty-year
            span reads as a histogram and a two-year one reads as two bars
            rather than as a wall of colour claiming a whole century.
          */}
          <div className="flex h-[70px] items-end gap-px">
            {years.map((entry) => {
              const empty = entry.count === 0
              const height = empty ? 4 : Math.max(6, Math.round((entry.count / peak) * 100))
              const note = !entry.held
                ? 'no edition held'
                : empty
                  ? 'held, no match'
                  : `${n(entry.count)} results`
              return (
                <button
                  key={entry.year}
                  type="button"
                  title={`${entry.year}: ${note}`}
                  onClick={() =>
                    onChange({
                      ...state,
                      date_from: `${entry.year}-01-01`,
                      date_to: `${entry.year}-12-31`,
                      offset: 0,
                    })
                  }
                  aria-label={`${entry.year}, ${note}`}
                  className="min-w-0 max-w-[10px] flex-1 origin-bottom p-0"
                  style={{
                    height: `${height}%`,
                    background: !entry.held
                      ? 'var(--color-accent-2-700)'
                      : empty
                        ? 'var(--color-neutral-400)'
                        : 'var(--jl-accent)',
                    opacity: empty ? 1 : 0.34 + 0.66 * (entry.count / peak),
                  }}
                />
              )
            })}
          </div>
          <div
            className="relative mt-[3px] h-[5px]"
            style={{ background: 'var(--jl-accent-200)' }}
            aria-hidden="true"
          >
            <div className="absolute inset-y-0 left-0 w-full" style={{ background: 'var(--jl-accent)' }} />
            <div className="absolute -left-px -top-[3px] h-[11px] w-[3px] bg-ink" />
            <div className="absolute -right-px -top-[3px] h-[11px] w-[3px] bg-ink" />
          </div>
          <div className="mt-[5px] flex justify-between font-mono text-[9.5px] text-ink2">
            {ticks.map((tick) => (
              <div key={tick}>{tick}</div>
            ))}
          </div>
        </>
      )}

      <div className="mt-[9px] flex gap-1.5">
        <input
          type="date"
          aria-label="Earliest date"
          value={state.date_from}
          onChange={(event) => onChange({ ...state, date_from: event.target.value, offset: 0 })}
          className="min-w-0 flex-1 rounded-[--radius-md] border border-rule2 bg-paper px-[7px] py-1 font-mono text-[11px] text-ink"
        />
        <input
          type="date"
          aria-label="Latest date"
          value={state.date_to}
          onChange={(event) => onChange({ ...state, date_to: event.target.value, offset: 0 })}
          className="min-w-0 flex-1 rounded-[--radius-md] border border-rule2 bg-paper px-[7px] py-1 font-mono text-[11px] text-ink"
        />
      </div>

      {facets?.licence?.label && (
        <div className="mt-[7px] font-mono text-[9.5px] uppercase leading-[1.5] text-mark">
          {facets.licence.label}
        </div>
      )}
    </div>
  )
}

/** First, last and two inner years, so the axis reads without crowding. */
function axisTicks(years: { year: number }[]): number[] {
  if (years.length === 0) return []
  if (years.length <= 4) return years.map((entry) => entry.year)
  const last = years.length - 1
  return [0, Math.round(last / 3), Math.round((2 * last) / 3), last].map(
    (index) => years[index].year,
  )
}

/** The board sets five or six rows to a group; a long tail folds behind a count. */
const FACET_ROWS = 6

function FacetGroup({
  title,
  rows,
  selected,
  onToggle,
}: {
  title: string
  rows: FacetValue[] | undefined
  selected: string[]
  onToggle: (value: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  if (!rows || rows.length === 0) return null
  // A selected value is always visible, wherever it sits in the tail:
  // a filter the reader cannot see is a filter they cannot remove.
  const head = rows.slice(0, FACET_ROWS)
  const pinned = rows.filter(
    (row) => selected.includes(row.value) && !head.some((entry) => entry.value === row.value),
  )
  const shown = expanded ? rows : [...head, ...pinned]
  const hidden = rows.length - shown.length

  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.11em] text-ink2">
        {title}
      </div>
      <div className="flex flex-col gap-[5px]">
        {shown.map((row) => {
          const on = selected.includes(row.value)
          return (
            <button
              key={row.value || row.label || 'unattributed'}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(row.value)}
              className="flex w-full items-center gap-2 bg-transparent p-0 text-left"
            >
              <span
                className="flex h-3 w-3 flex-none items-center justify-center rounded-[1px] text-[9px] leading-none"
                style={{
                  border: `1px solid ${on ? 'var(--jl-accent)' : 'var(--color-neutral-400)'}`,
                  background: on ? 'var(--jl-accent)' : 'var(--color-bg)',
                  color: 'var(--color-bg)',
                }}
                aria-hidden="true"
              >
                {on ? '✓' : ''}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] text-ink">
                {row.label ?? row.value}
              </span>
              <span className="flex-none font-mono text-[10.5px] text-ink2">{n(row.count)}</span>
            </button>
          )
        })}
        {(hidden > 0 || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="self-start bg-transparent p-0 font-mono text-[10px] uppercase tracking-[0.08em] text-accent"
          >
            {expanded ? 'Fewer' : `${n(hidden)} more`}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * The confidence floor.
 *
 * The sentence under it says what raising it hides and how many results sit
 * below the line, because a reader raising a recognition threshold is deciding
 * what to stop seeing and is owed the size of that decision. The figure comes
 * from the counted confidence bands; with no bands counted, the sentence says
 * only what raising it does.
 */
function ConfidenceFloor({
  state,
  facets,
  percent,
  onChange,
}: {
  state: SearchState
  facets: Facets | undefined
  percent: number
  onChange: (next: SearchState) => void
}) {
  const below = facets?.confidence_bands?.find((band) => band.value === 'below 0.70')?.count
  return (
    <div>
      <div className="mb-[9px] flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink2">
          Confidence at least
        </div>
        <div className="font-mono text-[11px] text-ink">
          {state.min_confidence ? `${percent}%` : 'any'}
        </div>
      </div>
      <input
        type="range"
        min={50}
        max={100}
        step={5}
        value={percent}
        aria-label="Minimum recognition confidence"
        onChange={(event) => {
          const value = Number(event.target.value)
          onChange({
            ...state,
            min_confidence: value <= 50 ? '' : (value / 100).toFixed(2),
            offset: 0,
          })
        }}
        className="h-[5px] w-full appearance-none rounded-[1px] bg-n300 accent-accent"
        style={{
          background: `linear-gradient(to right, var(--jl-accent) ${((percent - 50) / 50) * 100}%, var(--color-neutral-300) ${((percent - 50) / 50) * 100}%)`,
        }}
      />
      <div className="mt-1.5 flex justify-between font-mono text-[9.5px] text-ink2">
        <div>50%</div>
        <div>100%</div>
      </div>
      <p className="mt-2 text-[13.5px] leading-[1.45] text-ink2">
        Raising this hides scanned material first.
        {below !== undefined && below > 0 ? ` ${n(below)} results fall below 70%.` : ''}
      </p>
    </div>
  )
}
